import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { BookingWindowConfiguration, MembershipTierBookingWindow, MembershipTier } from '../../types';
import { membershipTierAPI } from '../../services/api';

interface BookingWindowConfigurationProps {
  title: string;
  bookingWindows?: BookingWindowConfiguration;
  onBookingWindowsChange: (bookingWindows: BookingWindowConfiguration) => void;
}

const BookingWindowConfigurationComponent: React.FC<BookingWindowConfigurationProps> = ({
  title,
  bookingWindows = {},
  onBookingWindowsChange
}) => {
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMembershipTiers();
  }, []);

  const fetchMembershipTiers = async () => {
    try {
      const response = await membershipTierAPI.getMembershipTiers(true);
      setMembershipTiers(response.data.filter((tier: MembershipTier) => tier.active));
      setError(null);
    } catch (err) {
      console.error('Error fetching membership tiers:', err);
      setError('Failed to load membership tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleBookingWindowChange = (tierName: string, field: keyof MembershipTierBookingWindow, value: any) => {
    const updatedWindows = {
      ...bookingWindows,
      [tierName]: {
        ...bookingWindows[tierName],
        [field]: value
      }
    };
    onBookingWindowsChange(updatedWindows);
  };

  const removeBookingWindow = (tierName: string) => {
    const { [tierName]: removed, ...remainingWindows } = bookingWindows;
    onBookingWindowsChange(remainingWindows);
  };

  const addBookingWindow = (tierName: string) => {
    const updatedWindows = {
      ...bookingWindows,
      [tierName]: {
        advanceBookingDays: 7,
        unlimited: false
      }
    };
    onBookingWindowsChange(updatedWindows);
  };

  if (loading) return <Typography>Loading membership tiers...</Typography>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure booking windows for different membership tiers. Members can book this amenity/class up to the specified number of days in advance.
        </Typography>

        <Box>
          {membershipTiers.map((tier) => {
            const hasWindow = bookingWindows[tier.name];
            const window = bookingWindows[tier.name] || { advanceBookingDays: 7, unlimited: false };
            
            return (
              <Box key={tier.id} sx={{ border: '1px solid', borderColor: 'divider', p: 2, borderRadius: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {tier.name}
                  </Typography>
                  {hasWindow ? (
                    <IconButton 
                      color="error" 
                      size="small" 
                      onClick={() => removeBookingWindow(tier.name)}
                      title="Remove booking window configuration"
                    >
                      <DeleteIcon />
                    </IconButton>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addBookingWindow(tier.name)}
                    >
                      Add Window
                    </Button>
                  )}
                </Box>

                {hasWindow && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={window.unlimited || false}
                          onChange={(e) => handleBookingWindowChange(tier.name, 'unlimited', e.target.checked)}
                        />
                      }
                      label="Unlimited advance booking"
                    />
                    
                    {!window.unlimited && (
                      <TextField
                        label="Advance Booking Days"
                        type="number"
                        value={window.advanceBookingDays}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          if (!isNaN(value) && value > 0) {
                            handleBookingWindowChange(tier.name, 'advanceBookingDays', value);
                          }
                        }}
                        size="small"
                        sx={{ minWidth: 200 }}
                        InputProps={{
                          inputProps: { min: 1, max: 365 }
                        }}
                        helperText="Days in advance members can book"
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {Object.keys(bookingWindows).length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No booking windows configured. Add booking windows to control how far in advance different membership tiers can book this amenity/class.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default BookingWindowConfigurationComponent;
